import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { absolute } from '../lib/api';
import { useCachedMediaUrl } from '../lib/useRangePlayer';
import type { Media } from '../types';

/** One interface over <video>/<audio> and the YouTube IFrame player (see docs: licensing plan B). */
export interface PlayerApi {
  play(): void;
  pause(): void;
  seek(sec: number): void;
  time(): number;
  duration(): number;
  paused(): boolean;
  setRate(rate: number): void;
}

interface Props {
  ref?: Ref<PlayerApi>;
  media: Media;
  mediaType: 'video' | 'audio';
  poster?: string | null;
  title: string;
  programTitle: string;
  startAt?: number;
  onTick(sec: number): void;
  onDuration?(sec: number): void;
  onPlayState?(playing: boolean): void;
  /** Play the small audio-only copy instead of the video (slow networks). */
  audioOnly?: boolean;
  onRequestAudio?(): void;
}

/** Renders inside the prototype's .video-stage frame. */
export function Player(props: Props) {
  if (props.media.kind === 'file') {
    const { url, audioUrl } = props.media;
    const useAudio = Boolean(props.audioOnly && audioUrl);
    return <FilePlayer {...props} url={absolute(useAudio ? audioUrl : url)} mediaType={useAudio ? 'audio' : props.mediaType} hasAudioVariant={Boolean(audioUrl)} />;
  }
  if (props.media.kind === 'youtube') return <YouTubePlayer {...props} videoId={props.media.youtubeId} />;
  const note =
    props.media.kind === 'locked'
      ? props.media.reason === 'copyright'
        ? '登录测试账号后播放视频。现在可以先阅读字幕、学习表达。'
        : '登录后播放'
      : '这一集的视频还没有准备好';
  return <AudioArt {...props} note={note} />;
}

/** Cover art shown for audio-only or unavailable media (same look as the prototype). */
function AudioArt({ poster, title, programTitle, note }: Props & { note?: string }) {
  return (
    <div className="audio-art">
      <div className="audio-art-text">
        <span className="stage-label">REAL VOICES · YOUR ENGLISH</span>
        <h1>{title}</h1>
        <span>{note ?? programTitle}</span>
      </div>
      {poster && <img src={absolute(poster)} alt="" />}
      <div className="stage-texture" />
    </div>
  );
}

/** requestAnimationFrame ticks: timeupdate (~4 Hz) is too coarse to stop exactly at a sentence end. */
function useTicker(active: boolean, read: () => number, onTick: (s: number) => void) {
  const cb = useRef(onTick);
  cb.current = onTick;
  useEffect(() => {
    if (!active) return;
    let id = 0;
    const loop = () => {
      cb.current(read());
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, [active, read]);
}

function FilePlayer(props: Props & { url: string; hasAudioVariant: boolean }) {
  const { ref, mediaType, poster, startAt, onTick, onDuration, onPlayState, hasAudioVariant, onRequestAudio } = props;
  // An episode already downloaded in 精听 plays from the device.
  const url = useCachedMediaUrl(props.url);
  const el = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  // "loading" = play was requested but no frames yet; "slow" = still loading after 12 s.
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const resumeAt = useRef<number | null>(null);
  const wantPlay = useRef(false);
  const readTime = useRef(() => el.current?.currentTime ?? 0).current;

  // Switching video ↔ audio keeps the position and the play state.
  const lastUrl = useRef(url);
  if (lastUrl.current !== url) {
    resumeAt.current = el.current?.currentTime ?? null;
    wantPlay.current = Boolean(el.current && !el.current.paused);
    lastUrl.current = url;
  }

  useEffect(() => {
    if (!loading) return setSlow(false);
    const t = setTimeout(() => setSlow(true), 12_000);
    return () => clearTimeout(t);
  }, [loading]);

  const play = () => {
    setLoading(true);
    void el.current?.play().catch(() => setLoading(false));
  };
  useImperativeHandle(ref, () => ({
    play,
    pause: () => el.current?.pause(),
    seek: (s) => {
      if (el.current) el.current.currentTime = Math.max(0, s);
      onTick(Math.max(0, s));
    },
    time: () => el.current?.currentTime ?? 0,
    duration: () => el.current?.duration || 0,
    paused: () => el.current?.paused ?? true,
    setRate: (r) => {
      if (el.current) el.current.playbackRate = r;
    },
  }));
  useTicker(playing, readTime, onTick);
  return (
    <>
      {mediaType === 'audio' && <AudioArt {...props} note={hasAudioVariant && props.audioOnly ? '仅音频模式 · 省流量，适合网络慢时精听' : undefined} />}
      <div id="video-mount" hidden={mediaType === 'audio'}>
        {/* A <video> element plays audio files too, so one element serves both. */}
        <video
          ref={el}
          src={url}
          poster={mediaType === 'video' ? absolute(poster) : undefined}
          preload="metadata"
          playsInline
          onClick={() => (el.current?.paused ? play() : el.current?.pause())}
          onLoadedMetadata={() => {
            const v = el.current;
            if (!v) return;
            onDuration?.(v.duration);
            const at = resumeAt.current ?? startAt;
            if (at && at > 0 && at < v.duration - 2) v.currentTime = at;
            resumeAt.current = null;
            onTick(v.currentTime);
            if (wantPlay.current) {
              wantPlay.current = false;
              play();
            }
          }}
          onPlay={() => {
            setPlaying(true);
            onPlayState?.(true);
          }}
          onPlaying={() => setLoading(false)}
          onWaiting={() => setLoading(true)}
          onPause={() => {
            setPlaying(false);
            setLoading(false);
            onPlayState?.(false);
          }}
          onEnded={() => setPlaying(false)}
          onError={() => setLoading(false)}
          onSeeked={() => onTick(el.current?.currentTime ?? 0)}
        />
      </div>
      {loading && (
        <div className="stage-loading">
          <span className="spinner" />
          <span>{slow ? '网络较慢，视频还在加载…' : '加载中…'}</span>
          {slow && hasAudioVariant && mediaType === 'video' && onRequestAudio && (
            <button className="small" onClick={onRequestAudio}>
              切换为仅音频（更快）
            </button>
          )}
        </div>
      )}
    </>
  );
}

// --- YouTube -----------------------------------------------------------------

interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(s: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  setPlaybackRate(r: number): void;
  destroy(): void;
}
declare global {
  interface Window {
    YT?: { Player: new (el: HTMLElement, opts: unknown) => YTPlayer; PlayerState: { PLAYING: number } };
    onYouTubeIframeAPIReady?: () => void;
  }
}

let ytReady: Promise<void> | null = null;
function loadYouTube(): Promise<void> {
  ytReady ??= new Promise((resolve) => {
    if (window.YT?.Player) return resolve();
    window.onYouTubeIframeAPIReady = () => resolve();
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(s);
  });
  return ytReady;
}

function YouTubePlayer({ ref, videoId, startAt, onTick, onDuration, onPlayState }: Props & { videoId: string }) {
  const host = useRef<HTMLDivElement>(null);
  const yt = useRef<YTPlayer | null>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    let disposed = false;
    void loadYouTube().then(() => {
      if (disposed || !host.current || !window.YT) return;
      const target = document.createElement('div');
      host.current.appendChild(target);
      yt.current = new window.YT.Player(target, {
        videoId,
        playerVars: { playsinline: 1, rel: 0, modestbranding: 1, cc_load_policy: 0, start: Math.floor(startAt ?? 0) },
        events: {
          onReady: () => onDuration?.(yt.current?.getDuration() ?? 0),
          onStateChange: (e: { data: number }) => {
            const isPlaying = e.data === window.YT!.PlayerState.PLAYING;
            setPlaying(isPlaying);
            onPlayState?.(isPlaying);
          },
        },
      });
    });
    return () => {
      disposed = true;
      yt.current?.destroy();
      yt.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);
  const readTime = useRef(() => yt.current?.getCurrentTime() ?? 0).current;
  useTicker(playing, readTime, onTick);
  useImperativeHandle(ref, () => ({
    play: () => yt.current?.playVideo(),
    pause: () => yt.current?.pauseVideo(),
    seek: (s) => {
      yt.current?.seekTo(Math.max(0, s), true);
      onTick(Math.max(0, s));
    },
    time: () => yt.current?.getCurrentTime() ?? 0,
    duration: () => yt.current?.getDuration() ?? 0,
    paused: () => !playing,
    setRate: (r) => yt.current?.setPlaybackRate(r),
  }));
  return <div id="video-mount" ref={host} className="yt-host" />;
}
